import { Component, Inject, inject, OnInit, ViewChild } from "@angular/core";
import {
    AsyncPipe,
    CommonModule,
    DatePipe,
    NgOptimizedImage,
    NgTemplateOutlet,
} from "@angular/common";
import { combineLatest, Observable, startWith, Subject } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { UserDm } from "@/_models";
import { USER_IS_AT_LEAST_ADMIN, UserService } from "@/_services";
import { OnboardingStateService } from "@/_services/onboarding-state.service";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { HomeCrewPerformanceComponent } from "@/components/home/home-crew-performance/home-crew-performance.component";
import { HomeAsapPerformanceComponent } from "@/components/home/home-asap-performance/home-asap-performance.component";
import { DropdownModule } from "primeng/dropdown";
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from "@angular/forms";
import { AddonFeature } from "@/_models/addon-features";
import {
    debounceTime,
    filter,
    switchMap,
    takeUntil,
    tap,
} from "rxjs/operators";
import { AccountService } from "@/_services/account.service";
import { isAsapAccount } from "@/_models/account.interface";
import { IssueService } from "@/_services/issue.service";
import { PromotionService } from "@/_services/promotion.service";
import { Issue } from "@/_models/issue.interface";
import { Promotion } from "@/_models/promotion.interface";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { SortingHomeService } from "@/_services/sorting-home.service";
import { HomeDateFilterComponent } from "./home-date-filter/home-date-filter.component";
import { HomeFilterService } from "@/_services/home-filter.service";
import { CombinedTasksService } from "@/_services/combined-task.service";
import { MessagesModule } from "primeng/messages";
import { Button } from "primeng/button";
import UserModel = UserDm.UserModel;
import { OnboardingStatus } from "@/_models/onboarding-status.interface";
import { QuickStartGuideService } from "@/_services/quick-start-guide.service";

@Component({
    selector: "app-home",
    standalone: true,
    imports: [
        AsyncPipe,
        DatePipe,
        NgOptimizedImage,
        SharedComponentsModule,
        RouterOutlet,
        HomeCrewPerformanceComponent,
        HomeAsapPerformanceComponent,
        DropdownModule,
        FormsModule,
        OverlayPanelModule,
        NgTemplateOutlet,
        HomeDateFilterComponent,
        CommonModule,
        ReactiveFormsModule,
        MessagesModule,
        Button,
    ],
    templateUrl: "./home.component.html",
    styleUrl: "./home.component.scss",
})
export class HomeComponent implements OnInit {
    private userService = inject(UserService);
    private router = inject(Router);
    private accountService = inject(AccountService);
    private onboardingState = inject(OnboardingStateService);
    private issueService = inject(IssueService);
    private promoService = inject(PromotionService);
    public homeFilterService = inject(HomeFilterService); // Make public to use in template
    private quickStartGuideService = inject(QuickStartGuideService);

    @ViewChild(HomeDateFilterComponent)
    dateFilterComponent: HomeDateFilterComponent;

    currentUser$: Observable<UserModel>;
    canUseTasks$: Observable<boolean>;
    filtersApplied$: Observable<boolean>; // Declare the new observable here
    private destroy$ = new Subject<void>();

    currentDate: Date;
    isTasksRoute = false;
    filterForm!: FormGroup; // Still reference it for convenience
    appliedDateFilter: string = "next7Days"; // Track the applied filter
    asapFormId: string = "";
    onboardingStatus: OnboardingStatus | null = null;

    currentQuote: string = "";
    quoteList: string[] = [
        "Safety isn't a rulebook, it's a mindset.",
        "Safety first is safety always.",
        "The safest risk is the one you've planned for.",
        "You can't manage what you don't measure.",
        "The best safety tool is a well-trained mind.",
        "Awareness is the first step toward accident prevention.",
        "Risk management is the art of staying in control.",
        "A culture of safety starts with leadership.",
        "Good safety habits are built one decision at a time.",
        "Consistent safety practices prevent costly mistakes.",
        "Plan ahead—accidents don't give warnings.",
        "Better a thousand safety checks than one fatal mistake.",
        "Predict the risk, prevent the loss.",
        "Protect what matters—your life, your team, your future.",
        "Near misses are warnings—heed them.",
        "Complacency is the enemy of safety.",
        "Safety is not just a priority—it's a core value.",
        "Every risk ignored is a hazard waiting to happen.",
        "Vigilance today prevents disasters tomorrow.",
        "The best defense against risk is preparation.",
    ];

    formType = [
        { label: "All Types", value: null },
        { label: "Assessments", value: "assessment" },
        { label: "Hazards", value: "hazard" },
        { label: "Incidents", value: "incident" },
        { label: "Reports", value: "flights" },
        { label: "Promotions", value: "promotions" },
        { label: "Mitigations", value: "issues" },
        { label: "Tasks", value: "tasks" },
    ];
    selectedFormType: string | null = null; // This might become redundant, consider removing if only using the formGroup directly
    timeFrameControl: FormControl<number> = new FormControl<number>(30);
    // Boolean flag for ASAP accounts for use in templates
    isAsap: boolean = false;
    showUpgradeBanner = true;
    showAcsfCard = true;

    quickActionList = [
        { label: "File Report", onClick: () => this.fileReport() },
        {
            label: "Start Risk Analysis",
            onClick: () => this.startCorrectiveAction(),
        },
        { label: "Send Promotion", onClick: () => this.sendPromotion() },
        {
            label: "Manage Safety Objectives",
            onClick: () => this.manageSafetyObjectives(),
        },
        {
            label: "Manage Crew Members",
            onClick: () => this.manageCrewMembers(),
        },
    ];

    navTabList = [
        { label: "Tasks", link: "tasks" },
        /*{ label: "Mentions", link: "mentions" },*/
        { label: "Promotions", link: "promotions" },
    ];

    constructor(
        @Inject(USER_IS_AT_LEAST_ADMIN) public isAdmin$: Observable<boolean>,
        private sortingService: SortingHomeService,
        private homeService: CombinedTasksService,
    ) {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                this.homeService.reload$.next();
            });
    }

    ngOnInit(): void {
        this.currentDate = new Date();
        this.currentUser$ = this.userService.getCurrentUserAsync();
        this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            this.isAsap = isAsapAccount(user.account);
        });
        this.currentQuote =
            this.quoteList[Math.floor(Math.random() * this.quoteList.length)];

        this.isTasksRoute = this.router.url.includes("/home/tasks");

        // Subscribe to ASAP Form ID from shared state
        this.onboardingState.asapFormId$
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => {
                this.asapFormId = id;
                // Fetch onboarding status when asapFormId changes
                if (this.isAsap && id) {
                    this.userService
                        .getOnboardingStatus(id)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe((status) => {
                            this.onboardingStatus = status;
                        });
                }
            });

        // Subscribe to route changes to update the flag dynamically
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                const isNowTasksRoute = this.router.url.includes("/home/tasks");

                // If navigating away from tasks, clear filters
                if (this.isTasksRoute && !isNowTasksRoute) {
                    this.clearFilters();
                }

                this.isTasksRoute = isNowTasksRoute;

                if (isNowTasksRoute) {
                    this.homeService.reload$.next(); // Emit to reload
                }
            });

        // Assign the filterForm from the service
        this.filterForm = this.homeFilterService.form;

        // Initialize filtersApplied$ as a reactive observable
        this.filtersApplied$ = combineLatest([
            this.homeFilterService.hasDateFilter$, // Observable for date filter status
            this.filterForm
                .get("formType")!
                .valueChanges.pipe(
                    startWith(this.filterForm.get("formType")!.value),
                ), // Observable for formType
            this.filterForm
                .get("pastDue")!
                .valueChanges.pipe(
                    startWith(this.filterForm.get("pastDue")!.value),
                ), // Observable for pastDue
            // Add other controls here if they contribute to the overall filter status
        ]).pipe(
            map(([hasDateFilter, formType, pastDue]) => {
                // Combine all filter states
                return hasDateFilter || !!formType || pastDue;
            }),
        );

        this.filterForm.valueChanges
            .pipe(debounceTime(300))
            .subscribe(() => this.homeService.reload$.next());

        // Load persisted dismiss states from dismissedBanners array
        this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            if (user?.dismissedBanners) {
                this.showAcsfCard = !user.dismissedBanners.includes("acsfCard");
                this.showUpgradeBanner =
                    !user.dismissedBanners.includes("upgradeBanner");
            }
        });

        this.canUseTasks$ = this.accountService
            .isFeatureEnabled(AddonFeature.hazardReporting)
            .pipe(shareReplay());
    }

    getAssessmentUrl() {
        const asapFormId = this.onboardingState.getAsapFormIdSnapshot();
        return this.isAsap
            ? `tasks/createFlight?assessmentId=${asapFormId ?? ""}`
            : `tasks/createFlight?formFilter=all`;
    }

    submitAssessment() {
        this.router.navigateByUrl("/tasks/createFlight?formFilter=assessments");
    }

    submitReport() {
        this.router.navigateByUrl(this.getAssessmentUrl());
    }

    onFilterApplied(filterType: string): void {
        this.appliedDateFilter = filterType;
    }

    isPastDueFilter(): boolean {
        return this.appliedDateFilter === "pastDue";
    }

    fileReport() {
        const url = this.getAssessmentUrl();
        this.accountService
            .checkFormFeature(AddonFeature.SAFETY_ASSURANCE)
            .pipe(
                filter((result) => result),
                tap(() => this.router.navigateByUrl(this.getAssessmentUrl())),
            )
            .subscribe();
    }

    startCorrectiveAction() {
        this.accountService
            .checkFormFeature(AddonFeature.SAFETY_ASSURANCE)
            .pipe(
                filter((result) => result),
                switchMap(() => this.issueService.createIssue()),
                tap((issue: Issue) =>
                    this.router.navigateByUrl(
                        `/assurance/registry/${issue._id}`,
                    ),
                ),
            )
            .subscribe();
    }

    sendPromotion() {
        this.accountService
            .checkFormFeature(AddonFeature.SAFETY_PROMOTION)
            .pipe(
                filter((result) => result),
                switchMap(() => this.promoService.createPromotion()),
                tap((promotion: Promotion) =>
                    this.router.navigateByUrl(`/promotions/${promotion._id}`),
                ),
            )
            .subscribe();
    }

    manageSafetyObjectives() {
        this.accountService
            .checkFormFeature(AddonFeature.SAFETY_POLICY)
            .pipe(
                filter((result) => result),
                tap(() => this.router.navigateByUrl("/policies/objectives")),
            )
            .subscribe();
    }

    manageCrewMembers() {
        this.router.navigateByUrl("/profile/users");
    }

    openNotifications() {
        console.debug(
            "Notifications clicked (not implemented in this release)",
        );
    }

    onSortChange(event: any) {
        const sorting = { key: "flightDate", value: event.value };
        this.sortingService.updateSorting(sorting);
    }

    logEvent(event: any) {
        console.log(event);
    }

    clearFilters() {
        this.homeFilterService.resetFilters();
        if (this.dateFilterComponent) {
            this.dateFilterComponent.resetToDefault();
        }
        this.appliedDateFilter = "next7Days";
    }

    // UI helpers for banners/cards
    dismissUpgradeBanner() {
        this.showUpgradeBanner = false;
        this.userService.dismissBanner("upgradeBanner", true).subscribe();
    }

    goToUpgrade() {
        this.router.navigateByUrl("/profile/subscription");
    }

    dismissAcsf() {
        this.showAcsfCard = false;
        this.userService.dismissBanner("acsfCard", true).subscribe();
    }

    openAcsfGuide() {
        window.open("https://acsf.aero/asap-program/", "_blank");
    }

    startGuide(event: Event) {
        event.stopPropagation();
        this.quickStartGuideService.toggle(event);
    }

    loadPastDueTasks() {
        if (this.dateFilterComponent) {
            this.dateFilterComponent.setFilterType("pastDue");
        }
        this.appliedDateFilter = "pastDue";
    }

    createTask() {
        this.accountService
            .checkFormFeature(AddonFeature.hazardReporting)
            .pipe(
                filter((result) => result),
                tap(() => this.router.navigateByUrl("/home/tasks/new")),
            )
            .subscribe();
    }
}
